// What each report shows (owner, 2026-10-22 redesign): the groups and their tabs, the summary tiles,
// the metrics the trend chart can switch between, the breakdowns and how the share is drawn, and
// the columns of the details table. Field names mirror packages/domain/analytics/src/reports.ts.
import type { IconName } from "@ady/ui";
import type { Dim, SeriesPoint, Topic } from "./api";

export type Section = Topic | "ranking";
export type GroupId = "sales" | "money" | "operations";

export const GROUPS: { id: GroupId; icon: IconName; topics: Section[] }[] = [
  { id: "sales", icon: "chart", topics: ["sales", "products", "customers", "discounts"] },
  { id: "money", icon: "money", topics: ["payments", "profit", "returns"] },
  { id: "operations", icon: "box", topics: ["delivery", "inventory", "ranking"] },
];
export const groupOf = (s: Section) => GROUPS.find((g) => g.topics.includes(s)) ?? GROUPS[0]!;

export const SECTION_ICON: Record<Section, IconName> = {
  sales: "chart",
  products: "box",
  customers: "users",
  discounts: "tag",
  payments: "money",
  profit: "trending",
  returns: "refresh",
  delivery: "truck",
  inventory: "layers",
  ranking: "star",
  traffic: "eye",
};

export interface TileSpec {
  metric: string;
  icon: IconName;
  tone?: "success" | "danger" | "warning";
  /** Lower is better (costs, returns, money still owed): a rise shows red. */
  invert?: boolean;
}

export interface TopicView {
  tiles: TileSpec[];
  /** Metrics the trend chart can show (present in the day series). */
  chart: string[];
  /** Breakdowns offered, in order; the first is the default. */
  dims: Dim[];
  /** The metric the share card ranks by, per breakdown (else `shareBy`). */
  shareBy: string;
  shareByDim?: Partial<Record<Dim, string>>;
  /** Donut for a few keys (channel, method); bars for long lists. */
  donut?: Dim[];
}

export const VIEWS: Record<Topic, TopicView> = {
  sales: {
    tiles: [
      { metric: "net_paisa", icon: "money" },
      { metric: "orders", icon: "cart" },
      { metric: "average_order_paisa", icon: "receipt" },
      { metric: "collected_paisa", icon: "check", tone: "success" },
      { metric: "outstanding_paisa", icon: "clock", tone: "warning", invert: true },
    ],
    chart: ["net_paisa", "orders", "average_order_paisa", "units"],
    dims: ["channel", "district", "staff", "location"],
    shareBy: "net_paisa",
    donut: ["channel", "location"],
  },
  products: {
    tiles: [
      { metric: "units", icon: "box" },
      { metric: "gross_paisa", icon: "money" },
      { metric: "orders", icon: "cart" },
    ],
    chart: ["units", "gross_paisa", "orders"],
    dims: ["product", "category"],
    shareBy: "units",
    donut: ["category"],
  },
  customers: {
    tiles: [
      { metric: "customers", icon: "users" },
      { metric: "new_customers", icon: "plus", tone: "success" },
      { metric: "returning_customers", icon: "refresh" },
      { metric: "repeat_customers", icon: "star" },
      { metric: "net_paisa", icon: "money" },
    ],
    chart: [],
    dims: ["total"],
    shareBy: "net_paisa",
  },
  discounts: {
    tiles: [
      { metric: "discount_paisa", icon: "tag", tone: "warning", invert: true },
      { metric: "discounted_orders", icon: "cart" },
      { metric: "discount_rate_bp", icon: "percent", invert: true },
      { metric: "coupon_orders", icon: "receipt" },
      { metric: "net_paisa", icon: "money" },
    ],
    chart: ["discount_paisa", "discounted_orders"],
    dims: ["coupon"],
    shareBy: "discount_paisa",
  },
  payments: {
    tiles: [
      { metric: "net_paisa", icon: "money" },
      { metric: "collected_paisa", icon: "check", tone: "success" },
      { metric: "outstanding_paisa", icon: "clock", tone: "warning", invert: true },
      { metric: "refunded_paisa", icon: "refresh", tone: "danger", invert: true },
    ],
    chart: ["net_paisa", "collected_paisa", "outstanding_paisa"],
    dims: ["method", "channel"],
    shareBy: "net_paisa",
    donut: ["method", "channel"],
  },
  profit: {
    tiles: [
      { metric: "revenue_paisa", icon: "money" },
      { metric: "cogs_paisa", icon: "box", tone: "warning", invert: true },
      { metric: "gross_profit_paisa", icon: "trending", tone: "success" },
      { metric: "margin_bp", icon: "percent" },
    ],
    chart: ["gross_profit_paisa", "revenue_paisa", "cogs_paisa"],
    dims: ["product", "category", "channel"],
    shareBy: "gross_profit_paisa",
    donut: ["channel"],
  },
  returns: {
    tiles: [
      { metric: "returns", icon: "refresh", invert: true },
      { metric: "return_rate_bp", icon: "percent", tone: "warning", invert: true },
      { metric: "rto", icon: "truck", invert: true },
      { metric: "rto_rate_bp", icon: "percent", tone: "danger", invert: true },
      { metric: "return_value_paisa", icon: "money", invert: true },
    ],
    chart: ["returns", "rto", "return_value_paisa"],
    dims: ["courier", "district", "channel"],
    shareBy: "returns",
    donut: ["channel"],
  },
  delivery: {
    tiles: [
      { metric: "shipments", icon: "truck" },
      { metric: "delivered", icon: "check", tone: "success" },
      { metric: "delivery_rate_bp", icon: "percent", tone: "success" },
      { metric: "rto_rate_bp", icon: "refresh", tone: "danger", invert: true },
      { metric: "average_delivery_minutes", icon: "clock", invert: true },
    ],
    chart: ["shipments", "delivered", "rto"],
    dims: ["courier", "district"],
    shareBy: "shipments",
    donut: ["courier"],
  },
  inventory: {
    tiles: [
      { metric: "stock_value_cost_paisa", icon: "layers" },
      { metric: "stock_value_price_paisa", icon: "money" },
      { metric: "stock_units", icon: "box" },
      { metric: "units_sold", icon: "cart" },
      { metric: "low_stock_variants", icon: "triangle", tone: "warning", invert: true },
      { metric: "out_of_stock_variants", icon: "x", tone: "danger", invert: true },
    ],
    chart: ["units_sold"],
    dims: ["product", "category", "location"],
    shareBy: "stock_value_cost_paisa",
    donut: ["category", "location"],
  },
  traffic: {
    tiles: [
      { metric: "visitors", icon: "users" },
      { metric: "page_view", icon: "eye" },
      { metric: "product_view", icon: "box" },
      { metric: "add_to_cart", icon: "cart" },
      { metric: "purchase", icon: "check", tone: "success" },
      { metric: "conversion_bp", icon: "percent", tone: "success" },
    ],
    chart: ["visitors", "page_view", "product_view", "add_to_cart", "purchase"],
    dims: ["source", "device", "page", "product"],
    shareBy: "visitors",
    shareByDim: { product: "product_view", page: "page_view" },
    donut: ["source", "device"],
  },
};

/** Metrics that are rates or averages: never summed across days. */
const isRatio = (m: string) => m.endsWith("_bp") || m.startsWith("average_");

export type Bucket = "day" | "week" | "month";
export const bucketFor = (days: number): Bucket =>
  days > 120 ? "month" : days > 45 ? "week" : "day";

/** The day series grouped into weeks (from Sunday) or months; ratios are recomputed or averaged. */
export function bucketSeries(
  series: SeriesPoint[],
  bucket: Bucket,
): { start: string; points: SeriesPoint[] }[] {
  const out: { start: string; points: SeriesPoint[] }[] = [];
  for (const p of series) {
    const d = new Date(`${String(p.date)}T00:00:00Z`);
    const start =
      bucket === "day"
        ? String(p.date)
        : bucket === "month"
          ? `${String(p.date).slice(0, 7)}-01`
          : new Date(d.getTime() - d.getUTCDay() * 86_400_000).toISOString().slice(0, 10);
    const last = out[out.length - 1];
    if (last && last.start === start) last.points.push(p);
    else out.push({ start, points: [p] });
  }
  return out;
}

/** One metric over a bucket of days. */
export function bucketValue(points: SeriesPoint[], metric: string): number {
  const num = (p: SeriesPoint, k: string) => Number(p[k] ?? 0) || 0;
  const sum = (k: string) => points.reduce((a, p) => a + num(p, k), 0);
  if (metric === "average_order_paisa") {
    const live = sum("orders") - sum("cancelled");
    return live > 0 ? Math.round(sum("net_paisa") / live) : 0;
  }
  if (isRatio(metric)) {
    const days = points.filter((p) => num(p, metric) !== 0);
    return days.length ? Math.round(days.reduce((a, p) => a + num(p, metric), 0) / days.length) : 0;
  }
  return sum(metric);
}
